/**
 * The vocabulary of the audit trail.
 *
 * `audit_logs.entity` is a text column, and a text column with no vocabulary is how the
 * legacy database ended up with `users.role_code` holding seven role names that existed
 * in no table. The same drift had already started here: the attendance module wrote
 * `WorkTime` while every other module wrote snake_case, so "the whole history of this
 * record" needed to know both spellings to find anything.
 *
 * Naming it once, in the package both the API and the admin panel import, means a typo
 * is a compilation error rather than a row nobody will ever find again — and the audit
 * filter in the panel can be built from this list instead of from a hand-kept copy.
 *
 * **The names are the table names, singular.** Not the Prisma model, not the TypeScript
 * class: the table. It is the only spelling that stays true when the code is refactored,
 * and the only one an operator reading the database can match against what they see.
 *
 * The rule held everywhere except in two places, and both were found by checking it
 * mechanically rather than by reading — which is the point of writing it down here at all:
 *
 *  - `free_busy` named no table. The revocation deletes `free_busy_reporters` (and the
 *    `DEVICE` rows that hang off it), and `entityId` was already the `userId`, which is
 *    exactly that table's primary key. So the honest name was `free_busy_reporter`;
 *  - `setting` named no table either: the settings live in `admin_settings`. An operator
 *    grepping the audit trail for `setting` would have found rows pointing at a table
 *    that does not exist, which is the drift this list exists to prevent.
 *
 * Both were corrected on 2026-08-05, before any production history existed to be broken.
 * `smoke-settings.mjs` now checks every value in this list against the real tables, so the
 * next drift is a red line in the battery rather than a comment nobody re-reads.
 *
 * **A word in this list must also be a word somebody writes.** Naming a real table is not
 * enough, and that gap let two terms sit here for months meaning nothing: `job_run`, which
 * the panel offered as a filter that could only ever answer «nessun risultato» (P28), and
 * `calendar_outbox`, removed on 2026-08-16 for the opposite reason to `job_run`'s. Nobody
 * ever *acts* on the write queue: there is no retry route, the entries are reprojected by
 * machine from a booking already recorded as `calendar_event`, and the row itself already
 * carries whose calendar (`user_id`) and which phone applied it (`applied_by_device_id`).
 * Its suspended entries are a **state**, and a state belongs to the monitor
 * (`free-busy-monitor.service.ts`), not to the register that answers «who did what». So
 * the word went, and the filter option went with it — the panel builds the list from here.
 *
 * `check:vocabolario` in `apps/api` now fails if any term here is never written as
 * `entity: AUDIT_ENTITIES.X`. It carries no exception list, because when it was written
 * every one of the remaining terms passed: the day one needs an exemption is the day
 * somebody should have to argue for it in a diff.
 */
export const AUDIT_ENTITIES = {
    USER: 'user',
    /** Un gruppo che una persona si è fatta per sé (coda §26): nome e colleghi, visibile al solo proprietario. */
    PERSONAL_GROUP: 'personal_group',
    /** Un luogo che una persona si è salvata per le sue riunioni (coda §67): visibile al solo proprietario. */
    PERSONAL_PLACE: 'personal_place',
    /** Un sistema esterno che legge da BesteHR con una chiave (K675): APS-MES e chi verrà. */
    EXTERNAL_SERVICE: 'external_service',
    /** Una chiave di un sistema esterno: creata, ruotata, revocata. Il segreto non entra mai. */
    API_KEY: 'api_key',
    USER_DEVICE: 'user_device',
    /** L'interno di Beste Talk di una persona: dato, cambiato, spento, tolto (2026-11-02). */
    TALK_EXTENSION: 'talk_extension',
    /** Un telefono con Beste Talk: revocato dal Centralino. */
    TALK_DEVICE: 'talk_device',
    TALK_GROUP: 'talk_group',
    TALK_CITOFONO: 'talk_citofono',
    TALK_PREFERENZE: 'talk_preferenze',
    TALK_FISSO: 'talk_telefono_fisso',
    REFRESH_TOKEN: 'refresh_token',
    ROLE: 'role',
    DEPARTMENT: 'department',
    PRODUCTION_LINE: 'production_line',
    HEADQUARTER: 'headquarter',
    WORK_TIME: 'work_time',
    WORK_DAY_REVIEW: 'work_day_review',
    VALUTAZIONE: 'valutazione',
    JUSTIFICATION: 'justification',
    JUSTIFICATION_TYPE: 'justification_type',
    JUSTIFICATION_PROPOSAL: 'justification_proposal',
    LEAVE_BALANCE: 'leave_balance',
    EXPENSE_NOTE: 'expense_note',
    EXPENSE_NOTE_TYPE: 'expense_note_type',
    BUSINESS_TRIP: 'business_trip',
    CALENDAR_EVENT: 'calendar_event',
    FREE_BUSY_REPORTER: 'free_busy_reporter',
    CALENDAR_OUTBOX: 'calendar_outbox',
    MEETING_ROOM: 'meeting_room',
    SIGNAGE_DEVICE: 'signage_device',
    VEHICLE: 'vehicle',
    /** Una porta che si apre col telefono, e un telefono che la apre (K741). */
    DOOR: 'door',
    DOOR_KEY: 'door_key',
    /**
     * Una videochiamata, e l'account della saletta che la gestisce dal PC della sala
     * (`spec/videochiamate.md`, 2026-10-26). Sulla chiamata finiscono creazione, termine e ogni
     * gesto del moderatore (ammettere, respingere, silenziare, allontanare, promuovere); sull'account
     * l'accesso della sala e la sua gestione dall'ufficio.
     */
    VIDEO_CALL: 'video_call',
    MEETING_ROOM_ACCOUNT: 'meeting_room_account',
    TRANSFER: 'transfer',
    TRANSFER_TRIP: 'transfer_trip',
    TRANSFER_LOCATION: 'transfer_location',
    TRANSFER_PACKAGE_TYPE: 'transfer_package_type',
    PAYCHECK: 'paycheck',
    CLOTHING_ITEM: 'clothing_item',
    UNIFORM_REQUEST: 'uniform_request',
    USER_CLOTHING: 'user_clothing',
    CONTACT: 'contact',
    CONVERSATION: 'conversation',
    MESSAGE: 'message',
    NOTIFICATION: 'notification',
    SETTING: 'admin_setting',
    JOB_RUN: 'job_run',
    SHIFT_WEEK: 'shift_week',
    /** Un turno pronto di un reparto (K807). */
    SHIFT_TEMPLATE: 'shift_template',
    /*
      Il menù della settimana, non l'ordine del singolo.
  
      Gli ordini **non** hanno un termine qui, ed è una scelta: un ordine dice che una certa
      persona un certo giorno era in una certa sede a mangiare, e ripeterlo nel registro di chi-ha-
      fatto-cosa vorrebbe dire tenere quel fatto in due posti invece che in uno (§116) — con la
      differenza che questo qui non si cancella con l'ordine. Il menù invece è un atto d'ufficio:
      lo carica la reception, vale per tutti, e se un giorno il menù è sbagliato la prima domanda
      è chi l'ha caricato e quando.
    */
    CANTEEN_MENU: 'canteen_menu',
    /*
      Il listino della mensa: un atto d'ufficio come il menù, e con dentro dei **soldi**.
      Un prezzo cambiato tocca il conto di ogni pasto ordinato da quel giorno in poi, e la prima
      domanda davanti a un totale che non torna è chi l'ha cambiato e quando.
    */
    CANTEEN_PRICE_LIST: 'canteen_price_list',
    /*
      La rettifica della mensa: l'importo di un giorno di una persona scritto a mano dalla
      reception. Soldi in busta paga, e un importo diverso da quello degli ordini: la prima
      domanda è chi l'ha scritto, quando e perché (2026-10-21).
    */
    CANTEEN_RETTIFICA: 'canteen_rettifica',
    DEPARTMENT_MANAGER: 'department_manager',
};
/** What to show a human in the audit filter. Italian, because the panel is Italian. */
export const AUDIT_ENTITY_LABELS = {
    [AUDIT_ENTITIES.PERSONAL_GROUP]: 'Gruppo personale',
    [AUDIT_ENTITIES.PERSONAL_PLACE]: 'Luogo personale',
    [AUDIT_ENTITIES.EXTERNAL_SERVICE]: 'Servizio esterno',
    [AUDIT_ENTITIES.API_KEY]: 'Chiave API',
    [AUDIT_ENTITIES.USER]: 'Utente',
    [AUDIT_ENTITIES.USER_DEVICE]: 'Dispositivo',
    [AUDIT_ENTITIES.TALK_EXTENSION]: 'Interno di Beste Talk',
    [AUDIT_ENTITIES.TALK_DEVICE]: 'Telefono di Beste Talk',
    [AUDIT_ENTITIES.TALK_GROUP]: 'Numero di gruppo di Beste Talk',
    [AUDIT_ENTITIES.TALK_CITOFONO]: 'Citofono di Beste Talk',
    [AUDIT_ENTITIES.TALK_PREFERENZE]: 'Non disturbare e inoltri di Beste Talk',
    [AUDIT_ENTITIES.TALK_FISSO]: 'Telefono fisso di Beste Talk',
    [AUDIT_ENTITIES.REFRESH_TOKEN]: 'Sessione',
    [AUDIT_ENTITIES.ROLE]: 'Ruolo',
    [AUDIT_ENTITIES.DEPARTMENT]: 'Reparto',
    [AUDIT_ENTITIES.PRODUCTION_LINE]: 'Linea di produzione',
    [AUDIT_ENTITIES.HEADQUARTER]: 'Sede',
    [AUDIT_ENTITIES.WORK_TIME]: 'Timbratura',
    [AUDIT_ENTITIES.WORK_DAY_REVIEW]: 'Rettifica di giornata',
    [AUDIT_ENTITIES.VALUTAZIONE]: 'Valutazione',
    [AUDIT_ENTITIES.JUSTIFICATION]: 'Giustificativo',
    [AUDIT_ENTITIES.JUSTIFICATION_TYPE]: 'Tipo giustificativo',
    [AUDIT_ENTITIES.JUSTIFICATION_PROPOSAL]: 'Proposta di ferie',
    [AUDIT_ENTITIES.LEAVE_BALANCE]: 'Monte ore',
    [AUDIT_ENTITIES.EXPENSE_NOTE]: 'Nota spese',
    [AUDIT_ENTITIES.EXPENSE_NOTE_TYPE]: 'Tipo nota spese',
    [AUDIT_ENTITIES.BUSINESS_TRIP]: 'Trasferta',
    [AUDIT_ENTITIES.CALENDAR_EVENT]: 'Evento calendario',
    [AUDIT_ENTITIES.FREE_BUSY_REPORTER]: 'Disponibilità da dispositivo',
    [AUDIT_ENTITIES.CALENDAR_OUTBOX]: 'Riunioni verso i telefoni',
    [AUDIT_ENTITIES.MEETING_ROOM]: 'Sala riunioni',
    [AUDIT_ENTITIES.SIGNAGE_DEVICE]: 'Cartello della sala',
    [AUDIT_ENTITIES.VEHICLE]: 'Veicolo',
    [AUDIT_ENTITIES.DOOR]: 'Porta',
    [AUDIT_ENTITIES.DOOR_KEY]: 'Chiave della porta',
    [AUDIT_ENTITIES.VIDEO_CALL]: 'Videochiamata',
    [AUDIT_ENTITIES.MEETING_ROOM_ACCOUNT]: 'Account di sala',
    [AUDIT_ENTITIES.TRANSFER]: 'Spedizione',
    [AUDIT_ENTITIES.TRANSFER_TRIP]: 'Viaggio',
    [AUDIT_ENTITIES.TRANSFER_LOCATION]: 'Luogo',
    [AUDIT_ENTITIES.TRANSFER_PACKAGE_TYPE]: 'Tipo di collo',
    [AUDIT_ENTITIES.PAYCHECK]: 'Busta paga',
    [AUDIT_ENTITIES.CLOTHING_ITEM]: 'Capo di vestiario',
    [AUDIT_ENTITIES.UNIFORM_REQUEST]: 'Richiesta divisa',
    [AUDIT_ENTITIES.USER_CLOTHING]: 'Taglia dipendente',
    [AUDIT_ENTITIES.CONTACT]: 'Contatto in rubrica',
    [AUDIT_ENTITIES.CONVERSATION]: 'Conversazione',
    [AUDIT_ENTITIES.MESSAGE]: 'Messaggio in chat',
    [AUDIT_ENTITIES.NOTIFICATION]: 'Comunicazione',
    [AUDIT_ENTITIES.SETTING]: 'Impostazione',
    [AUDIT_ENTITIES.JOB_RUN]: 'Elaborazione automatica',
    [AUDIT_ENTITIES.SHIFT_WEEK]: 'Settimana di turni',
    [AUDIT_ENTITIES.SHIFT_TEMPLATE]: 'Turno pronto del reparto',
    [AUDIT_ENTITIES.CANTEEN_MENU]: 'Menù della mensa',
    [AUDIT_ENTITIES.CANTEEN_PRICE_LIST]: 'Listino della mensa',
    [AUDIT_ENTITIES.CANTEEN_RETTIFICA]: 'Rettifica della mensa',
    [AUDIT_ENTITIES.DEPARTMENT_MANAGER]: 'Responsabile di reparto',
};
//# sourceMappingURL=audit.js.map